import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { match, P } from "ts-pattern";
import { useGetInvite } from "@/hooks/queries/use-get-invite";
import { AdminRegisterForm } from "../forms/admin-register-form";
import { ThemeSwitcher } from "../theme-switcher";
import { Button } from "../ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "../ui/card";

type RegistrationState = "idle" | "error" | "success";

export function RegisterPage() {
	const [registrationState, setRegistrationState] =
		useState<RegistrationState>("idle");
	const navigate = useNavigate();
	const { inviteCode } = useParams();
	const inviteQuery = useGetInvite(inviteCode);

	// Handle redirect for invalid invite
	useEffect(() => {
		if (inviteQuery.isFetched && (inviteQuery.isError || !inviteQuery.data)) {
			navigate("/login");
		}

		if (inviteQuery.data) {
			// Check if invite is internal type
			if (inviteQuery.data.type !== "internal") {
				navigate("/login");
			}

			if (inviteQuery.data.accepted) {
				setRegistrationState("error");
			}

			const now = new Date();
			const expires = new Date(inviteQuery.data.expires);
			if (now > expires) {
				setRegistrationState("error");
			}
		}
	}, [inviteQuery.isFetched, inviteQuery.isError, inviteQuery.data, navigate]);

	return (
		<>
			<div className="fixed top-4 right-4 z-50">
				<ThemeSwitcher />
			</div>
			{match({ query: inviteQuery, registration: registrationState })
				.with({ query: { isLoading: true } }, () => (
					<p>Loading Invitation...</p>
				))
				.with({ registration: "success" }, () => <RegisterSuccessCard />)
				.with({ registration: "error" }, () => <RegisterErrorCard />)
				.with({ query: { isSuccess: true, data: P.not(P.nullish) } }, () => (
					<AdminRegisterForm
						onSuccess={() => setRegistrationState("success")}
						onError={() => setRegistrationState("error")}
						invite={inviteQuery.data}
					/>
				))
				.otherwise(() => (
					<Navigate to="/login" />
				))}
		</>
	);
}

function RegisterErrorCard() {
	return (
		<Card className="max-w-1/2">
			<CardHeader>
				<h1 className="text-xl">Registration Error</h1>
			</CardHeader>
			<CardContent>
				<h2 className="text-lg">
					We ran into an unexpected error trying to create your admin account
				</h2>
				<p>
					Please try again. If the issue persists, send us an email at:{" "}
					<a
						className="underline text-blue-500"
						href="mailto:reachout@indivisibleeldorado.org"
					>
						reachout@indivisibleeldorado.org
					</a>
				</p>
			</CardContent>
			<CardFooter>
				<Button onClick={() => window.location.reload()}>Retry</Button>
			</CardFooter>
		</Card>
	);
}

function RegisterSuccessCard() {
	return (
		<Card className="max-w-1/2">
			<CardHeader>
				<h1 className="text-xl">Admin Account Created</h1>
			</CardHeader>
			<CardContent className="space-y-2">
				<h2 className="text-lg">
					Your admin account has been successfully created!
				</h2>
				<p>
					You can now log in to the admin dashboard using your email and
					password.
				</p>
				<p>
					<a className="underline text-blue-500" href="/login">
						Click here to log in
					</a>
				</p>
			</CardContent>
		</Card>
	);
}
